import React, { useContext, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  Menu,
  TextField,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import { doc, updateDoc, onSnapshot, collection, getDoc, deleteDoc, getDocs, setDoc, addDoc } from 'firebase/firestore';
import { deleteObject, ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { AppContext } from '../AppContext';
import { auth, db, storage } from '../firebaseConfig';
import { SnackbarContext } from '../SnackbarContext';
import { trackFeature } from '../utils/analytics';
import { resizeImageToMax } from '../utils/imageProcessing';
import ConfirmationDialog from './ConfirmationDialog';
import UserAvatar from './UserAvatar';
import {
  formatInviteCode,
  normalizeInviteCode,
  generateInviteCodeNormalized,
} from '../utils/inviteCode';
import {
  convertRecordsToCsv,
  createCsvZipBlob,
  extractCsvFiles,
  extractZipFiles,
  parseCsvToRecords,
  triggerZipDownload,
} from '../utils/csvExport';
import {
  downloadInvoiceBackup,
  importInvoiceBackup,
} from '../utils/invoiceBackup';
import { createBackupFilename } from '../utils/backupFilename';
import {
  createBackupMetadataFile,
  validateBackupMetadata,
} from '../utils/backupMetadata';

const Farm = () => {
  const {
    userData,
    farmData,
    customers,
    seeds,
    trays,
    harvests,
    orders,
    tasks,
    invoices,
  } = useContext(AppContext);
  const { showSuccess, showError } = useContext(SnackbarContext);
  const hasCollectionData = [
    customers,
    seeds,
    trays,
    harvests,
    orders,
    tasks,
  ].some((records) => records?.length > 0);

  const [memberRole, setMemberRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = useState(null);
  const [roleMenuMember, setRoleMenuMember] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [dialogAction, setDialogAction] = useState(null);
  const [busyMemberIds, setBusyMemberIds] = useState({});
  const [exportingBackup, setExportingBackup] = useState(false);
  const [importingBackup, setImportingBackup] = useState(false);
  const [exportingInvoices, setExportingInvoices] = useState(false);
  const [importingInvoices, setImportingInvoices] = useState(false);
  const [farmDangerDialogOpen, setFarmDangerDialogOpen] = useState(false);
  const [farmDangerTitle, setFarmDangerTitle] = useState('');
  const [farmDangerMessage, setFarmDangerMessage] = useState('');
  const [farmDangerAction, setFarmDangerAction] = useState(null);

  const [editingFarm, setEditingFarm] = useState(false);
  const [tempFarm, setTempFarm] = useState(farmData?.farmName || '');
  const [editingPhone, setEditingPhone] = useState(false);
  const [tempPhone, setTempPhone] = useState(farmData?.phone || '');
  const [editingEmail, setEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState(farmData?.email || '');
  const [editingAddress, setEditingAddress] = useState(false);
  const [tempAddress, setTempAddress] = useState({
    street: farmData?.address?.split(',')[0]?.trim() || '',
    city: farmData?.address?.split(',')[1]?.trim() || '',
    county: farmData?.address?.split(',')[2]?.trim() || '',
    postalCode: farmData?.address?.split(',')[3]?.trim() || '',
  });

  const [weightUnit, setWeightUnit] = useState(farmData?.settings?.weightUnit || 'grams');
  const [measureUnit, setMeasureUnit] = useState(farmData?.settings?.measureUnit || 'in');
  const [dateFormat, setDateFormat] = useState(farmData?.settings?.dateFormat || 'DD/MM/YYYY');
  const [calendarWeekStart, setCalendarWeekStart] = useState(
    farmData?.settings?.calendarWeekStart || 'sunday'
  );
  const [currency, setCurrency] = useState(farmData?.settings?.currency || '£');

  const uid = auth.currentUser?.uid;

  useEffect(() => {
    if (!userData?.activeFarmId || !uid) {
      setMemberRole(null);
      return;
    }

    const memberRef = doc(db, 'farms', userData.activeFarmId, 'members', uid);
    const unsubscribe = onSnapshot(memberRef, (snap) => {
      setMemberRole(snap.exists() ? snap.data()?.role || null : null);
    });

    return () => unsubscribe();
  }, [userData?.activeFarmId, uid]);

  useEffect(() => {
    if (!userData?.activeFarmId) {
      setMembers([]);
      return;
    }

    const membersRef = collection(db, 'farms', userData.activeFarmId, 'members');
    const unsubscribe = onSnapshot(
      membersRef,
      async (snapshot) => {
        const resolved = await Promise.all(
          snapshot.docs.map(async (memberSnap) => {
            const memberId = memberSnap.id;
            const memberData = memberSnap.data() || {};
            let name = memberData.displayName || memberId;
            let photoURL = memberData.photoURL || '';

            if (!memberData.displayName || !memberData.photoURL) {
              try {
                const userSnap = await getDoc(doc(db, 'users', memberId));
                if (userSnap.exists()) {
                  const userDocData = userSnap.data() || {};
                  name = userDocData.displayName || userDocData.email || memberId;
                  photoURL = userDocData.photoURL || '';
                }
              } catch {
                // Keep fallback id if user doc cannot be read.
              }
            }

            return {
              id: memberId,
              name,
              photoURL,
              role: memberData.role || 'member',
              joinedAt: memberData.joinedAt || null,
            };
          })
        );

        resolved.sort((a, b) => {
          const rolePriority = {
            owner: 0,
            admin: 1,
            worker: 2,
            member: 3,
          };

          const aRole = String(a.role || 'member').toLowerCase();
          const bRole = String(b.role || 'member').toLowerCase();
          const aPriority = Object.prototype.hasOwnProperty.call(rolePriority, aRole) ? rolePriority[aRole] : 99;
          const bPriority = Object.prototype.hasOwnProperty.call(rolePriority, bRole) ? rolePriority[bRole] : 99;

          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }

          const aMs = a.joinedAt?.toDate ? a.joinedAt.toDate().getTime() : 0;
          const bMs = b.joinedAt?.toDate ? b.joinedAt.toDate().getTime() : 0;
          return aMs - bMs;
        });

        setMembers(resolved);
      },
      () => {
        setMembers([]);
      }
    );

    return () => unsubscribe();
  }, [userData?.activeFarmId]);

  if (!userData?.activeFarmId) {
    return <Typography>No farm selected.</Typography>;
  }

  const farmDocRef = doc(db, 'farms', userData.activeFarmId);

  const handleSaveFarm = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can edit farm details.');
      return;
    }
    try {
      if (!tempFarm.trim()) {
        showError('Farm name cannot be empty.');
        return;
      }
      await updateDoc(farmDocRef, {
        farmName: tempFarm.trim(),
      });
      showSuccess('Farm name updated successfully.');
      trackFeature('farm_name_updated');
      setEditingFarm(false);
    } catch (err) {
      showError('Failed to update farm name.');
    }
  };

  const handleSavePhone = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can edit farm details.');
      return;
    }
    try {
      await updateDoc(farmDocRef, {
        phone: tempPhone,
      });
      showSuccess('Phone number updated successfully.');
      trackFeature('phone_number_updated');
      setEditingPhone(false);
    } catch (err) {
      showError('Failed to update phone number.');
    }
  };

  const handleSaveAddress = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can edit farm details.');
      return;
    }
    const { street, city, county, postalCode } = tempAddress;

    if (!street || !city || !county || !postalCode) {
      showError('Please fill in all address fields.');
      return;
    }

    const fullAddress = `${street}, ${city}, ${county}, ${postalCode}`;
    try {
      await updateDoc(farmDocRef, {
        address: fullAddress,
      });
      showSuccess('Address updated successfully.');
      trackFeature('farm_address_updated');
      setEditingAddress(false);
    } catch (err) {
      showError('Failed to update address.');
    }
  };

  const handleLogoUpload = async (e) => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can edit farm details.');
      return;
    }
    const file = e.target.files[0];
    if (!file || !userData?.activeFarmId) return;
    e.target.value = '';

    try {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showError('Please upload a PNG, JPG, or WEBP image.');
        return;
      }

      const maxOriginalSizeInBytes = 20 * 1024 * 1024;
      if (file.size > maxOriginalSizeInBytes) {
        showError('Original image size cannot exceed 20MB.');
        return;
      }

      const resizedBlob = await resizeImageToMax(file, 512);
      const maxUploadedSizeInBytes = 2 * 1024 * 1024;
      if (resizedBlob.size > maxUploadedSizeInBytes) {
        showError('The resized image is still larger than 2MB. Please choose a simpler image.');
        return;
      }

      const existingLogoFileName = farmData?.logoFileName;
      if (existingLogoFileName) {
        const oldLogoRef = ref(storage, `farms/${userData.activeFarmId}/logos/${existingLogoFileName}`);
        try {
          await deleteObject(oldLogoRef);
        } catch (err) {
          console.warn('Old logo delete failed:', err);
        }
      }

      const uniqueLogoFileName = `logo-${Date.now()}.png`;
      const logoRef = ref(storage, `farms/${userData.activeFarmId}/logos/${uniqueLogoFileName}`);
      const snapshot = await uploadBytes(logoRef, resizedBlob, {
        contentType: resizedBlob.type || 'image/png',
      });
      const downloadURL = await getDownloadURL(snapshot.ref);

      await updateDoc(farmDocRef, {
        logoUrl: downloadURL,
        logoFileName: uniqueLogoFileName,
      });

      showSuccess('Logo uploaded successfully.');
      trackFeature('logo_uploaded');
    } catch (error) {
      showError(`Failed to upload logo: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteLogo = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can edit farm details.');
      return;
    }
    if (!userData?.activeFarmId) return;
    try {
      const existingLogoFileName = farmData?.logoFileName;
      if (existingLogoFileName) {
        const oldLogoRef = ref(storage, `farms/${userData.activeFarmId}/logos/${existingLogoFileName}`);
        await deleteObject(oldLogoRef);
      }
      await updateDoc(farmDocRef, {
        logoUrl: '',
        logoFileName: '',
      });
      showSuccess('Logo deleted successfully.');
      trackFeature('logo_deleted');
    } catch (error) {
      showError('Failed to delete logo.');
    }
  };

  const handleSaveSettings = async () => {
    if (memberRole !== 'owner' && memberRole !== 'admin') {
      showError('Only owner/admin can update farm settings.');
      return;
    }
    try {
      await updateDoc(farmDocRef, {
        settings: { weightUnit, measureUnit, dateFormat, calendarWeekStart, currency },
      });
      showSuccess('Settings updated successfully.');
    } catch (error) {
      showError('Failed to update settings.');
    }
  };

  const handleRegenerateInviteCode = async () => {
    if (memberRole !== 'owner' && memberRole !== 'admin') {
      showError('Only owner/admin can manage invite code.');
      return;
    }
    if (!userData?.activeFarmId) return;
    const nextCode = generateInviteCodeNormalized();
    try {
      await updateDoc(farmDocRef, { inviteCode: nextCode });
      showSuccess('Invite code re-generated.');
    } catch (error) {
      showError('Failed to re-generate invite code.');
    }
  };

  const handleCopyInviteCode = () => {
    if (memberRole !== 'owner' && memberRole !== 'admin') {
      showError('Only owner/admin can manage invite code.');
      return;
    }
    const normalized = normalizeInviteCode(inviteCode);
    if (!normalized) return;
    navigator.clipboard.writeText(normalized)
      .then(() => showSuccess('Invite code copied.'))
      .catch(() => showError('Failed to copy invite code.'));
  };

  const handleSaveFarmEmail = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can edit farm details.');
      return;
    }
    try {
      await updateDoc(farmDocRef, {
        email: tempEmail,
      });
      showSuccess('Invoicing email updated successfully.');
      trackFeature('farm_invoice_email_updated');
      setEditingEmail(false);
    } catch (err) {
      showError('Failed to update invoicing email.');
    }
  };

  const handleEnterSave = (event, saveFn) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveFn();
    }
  };

  const handleRemoveMember = async (memberId, farmIdOverride = null) => {
    const targetFarmId = farmIdOverride || userData?.activeFarmId;
    if (!targetFarmId || !memberId) return;
    await deleteDoc(doc(db, 'farms', targetFarmId, 'members', memberId));
    showSuccess('Member removed.');
  };

  const stripInvoiceLinks = (record, collectionName) => {
    if (!record) {
      return record;
    }

    const cleaned = { ...record };
    if (collectionName === 'orders') {
      delete cleaned.invoiceUrl;
    }
    return cleaned;
  };

  const formatImportLabel = (baseName) => {
    if (!baseName) {
      return '';
    }
    return baseName.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  };

  const handleDownloadBackup = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can manage backups.');
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }

    setExportingBackup(true);

    try {
      const collectionsToExport = [
        { name: 'customers', label: 'customers' },
        { name: 'seeds', label: 'seeds' },
        { name: 'trays', label: 'trays' },
        { name: 'harvests', label: 'harvests' },
        { name: 'orders', label: 'orders' },
        { name: 'tasks', label: 'tasks' },
        { name: 'counters', label: 'counters' },
        { name: 'harvestTemplates', label: 'harvest_templates' },
        { name: 'orderNoteTemplates', label: 'order_note_templates' },
      ];

      const files = [{
        filename: 'metadata.json',
        content: new TextDecoder().decode(createBackupMetadataFile().data),
      }];

      for (const collectionConfig of collectionsToExport) {
        const snapshot = await getDocs(collection(db, 'farms', userData.activeFarmId, collectionConfig.name));
        const records = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...stripInvoiceLinks(docSnap.data(), collectionConfig.name),
        }));
        const csv = convertRecordsToCsv(records);
        files.push({ filename: `${collectionConfig.label}.csv`, content: csv });
      }

      const zipBlob = await createCsvZipBlob(files);
      const timestamp = new Date().toISOString().slice(0, 10);
      triggerZipDownload(
        createBackupFilename(farmData?.farmName, 'Data', timestamp),
        zipBlob
      );
      showSuccess('Your MicGro data backup is ready. The download should start shortly.');
    } catch {
      showError('Export failed. Please try again.');
    } finally {
      setExportingBackup(false);
    }
  };

  const handleUploadBackup = async (event) => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can manage backups.');
      event.target.value = '';
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.micgro') && !fileName.endsWith('.zip')) {
      showError('Please upload a MicGro backup or legacy ZIP backup.');
      event.target.value = '';
      return;
    }

    setImportingBackup(true);

    try {
      const archiveFiles = await extractZipFiles(file);
      validateBackupMetadata(archiveFiles);
      const zipFiles = extractCsvFiles(archiveFiles);
      const collectionMap = {
        customers: 'customers',
        seeds: 'seeds',
        trays: 'trays',
        harvests: 'harvests',
        orders: 'orders',
        tasks: 'tasks',
        counters: 'counters',
        harvest_templates: 'harvestTemplates',
        order_note_templates: 'orderNoteTemplates',
      };

      const importPromises = zipFiles.map(async (zipEntry) => {
        const fileName = zipEntry.filename.split('/').pop();
        if (!fileName) {
          return null;
        }

        const baseName = fileName.replace(/\.csv$/i, '');
        if (baseName === 'media' || baseName === 'user_profile') {
          return null;
        }

        const collectionName = collectionMap[baseName];
        if (!collectionName) {
          return null;
        }

        const records = parseCsvToRecords(zipEntry.content);

        for (const record of records) {
          const { id, ...data } = record || {};
          const cleanedData = stripInvoiceLinks(data, collectionName);
          const normalizedData = { ...cleanedData };
          if (collectionName === 'counters') {
            Object.keys(normalizedData).forEach((key) => {
              const value = normalizedData[key];
              if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
                normalizedData[key] = Number(value);
              }
            });
          }
          if (id) {
            await setDoc(doc(db, 'farms', userData.activeFarmId, collectionName, id), normalizedData, { merge: true });
          } else {
            await addDoc(collection(db, 'farms', userData.activeFarmId, collectionName), normalizedData);
          }
        }

        return formatImportLabel(baseName);
      });

      const importedCollections = (await Promise.all(importPromises)).filter(Boolean);
      if (importedCollections.length === 0) {
        showError('No importable data files were found in this backup.');
      } else {
        showSuccess(
          `Import complete. Data imported for: ${[...new Set(importedCollections)].join(', ')}.`
        );
      }
    } catch (error) {
      showError(error?.message || 'Import failed. Please check that the backup file is valid.');
    } finally {
      setImportingBackup(false);
      event.target.value = '';
    }
  };

  const handleDownloadInvoiceBackup = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can manage backups.');
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    if (!invoices?.length) {
      showError('No invoices are available to back up.');
      return;
    }

    setExportingInvoices(true);
    try {
      const { downloadedCount, skippedCount } = await downloadInvoiceBackup({
        invoices,
        activeFarmId: userData.activeFarmId,
        farmName: farmData?.farmName,
        storage,
      });

      if (downloadedCount === 0) {
        showError('No invoice PDFs could be downloaded.');
        return;
      }

      trackFeature('farm_invoice_backup_downloaded', {
        invoiceCount: downloadedCount,
        skippedCount,
      });

      if (skippedCount > 0) {
        showError(
          `Backed up ${downloadedCount} invoice${downloadedCount === 1 ? '' : 's'}; skipped ${skippedCount} missing or unavailable file${skippedCount === 1 ? '' : 's'}.`
        );
      } else {
        showSuccess(
          `Backed up ${downloadedCount} invoice${downloadedCount === 1 ? '' : 's'} as a MicGro backup.`
        );
      }
    } catch {
      showError('Failed to create the invoice backup.');
    } finally {
      setExportingInvoices(false);
    }
  };

  const handleUploadInvoiceBackup = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (memberRole !== 'owner') {
      showError('Only the farm owner can manage backups.');
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    if (!file) return;
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.micgro') && !fileName.endsWith('.zip')) {
      showError('Please upload a MicGro invoice backup or legacy ZIP backup.');
      return;
    }

    setImportingInvoices(true);
    try {
      const result = await importInvoiceBackup({
        backupFile: file,
        activeFarmId: userData.activeFarmId,
        db,
        storage,
      });

      trackFeature('farm_invoice_backup_imported', result);

      const summary = [
        `${result.importedCount} imported`,
        `${result.skippedCount} existing skipped`,
        `${result.failedCount} failed`,
      ].join(', ');

      if (result.importedCount > 0 && result.failedCount === 0) {
        showSuccess(`Invoice import complete: ${summary}.`);
      } else if (result.importedCount > 0) {
        showError(`Invoice import partially completed: ${summary}.`);
      } else {
        showError(`No invoices were imported: ${summary}.`);
      }
    } catch (error) {
      showError(error?.message || 'Invoice import failed.');
    } finally {
      setImportingInvoices(false);
    }
  };

  const openFarmDangerDialog = (title, message, action) => {
    setFarmDangerTitle(title);
    setFarmDangerMessage(message);
    setFarmDangerAction(action);
    setFarmDangerDialogOpen(true);
  };

  const handleConfirmFarmDanger = async () => {
    setFarmDangerDialogOpen(false);
    if (typeof farmDangerAction === 'function') {
      await farmDangerAction();
    }
    setFarmDangerAction(null);
  };

  const handleDeleteFarmData = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can delete farm data.');
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    openFarmDangerDialog(
      'Delete Farm Data',
      'Delete all farm data (seeds, harvests, trays, orders, tasks)? This cannot be undone. Type "MICROGREEN" to confirm.',
      async () => {
        try {
          const collectionsToDelete = ['seeds', 'harvests', 'orders', 'tasks', 'trays'];
          for (const col of collectionsToDelete) {
            const snap = await getDocs(collection(db, 'farms', userData.activeFarmId, col));
            for (const docSnap of snap.docs) {
              await deleteDoc(docSnap.ref);
            }
          }
          await deleteDoc(doc(db, 'farms', userData.activeFarmId, 'counters', 'orders'));
          showSuccess('All farm data deleted successfully.');
        } catch {
          showError('Failed to delete farm data.');
        }
      }
    );
  };

  const handleDeleteCustomers = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can delete customers.');
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    openFarmDangerDialog(
      'Delete All Customers',
      'Delete all customers for this farm? This cannot be undone. Type "MICROGREEN" to confirm.',
      async () => {
        try {
          const customersSnap = await getDocs(collection(db, 'farms', userData.activeFarmId, 'customers'));
          for (const cust of customersSnap.docs) {
            await deleteDoc(cust.ref);
          }
          showSuccess('All customers deleted successfully.');
        } catch {
          showError('Failed to delete customers.');
        }
      }
    );
  };

  const handleDeleteInvoices = async () => {
    if (memberRole !== 'owner') {
      showError('Only the farm owner can delete invoices.');
      return;
    }
    if (!userData?.activeFarmId) {
      showError('No active farm selected.');
      return;
    }
    openFarmDangerDialog(
      'Delete All Invoices',
      'Delete all invoices for this farm? This cannot be undone. Type "MICROGREEN" to confirm.',
      async () => {
        try {
          const invoicesSnap = await getDocs(collection(db, 'farms', userData.activeFarmId, 'invoices'));
          for (const invoice of invoicesSnap.docs) {
            await deleteDoc(invoice.ref);
          }
          const folderRef = ref(storage, `farms/${userData.activeFarmId}/invoices`);
          const result = await listAll(folderRef);
          for (const itemRef of result.items) {
            await deleteObject(itemRef);
          }
          showSuccess('All PDF invoices deleted successfully.');
        } catch {
          showError('Failed to delete invoices.');
        }
      }
    );
  };

  const roleOptions = ['admin', 'worker', 'member'];
  const activeDialogAction = pendingAction || dialogAction;
  const isPendingRoleChange = activeDialogAction?.type === 'changeRole';
  const isPendingRemoveMember = activeDialogAction?.type === 'removeMember';

  const handleOpenRoleMenu = (event, member) => {
    setRoleMenuAnchorEl(event.currentTarget);
    setRoleMenuMember(member);
  };

  const handleCloseRoleMenu = () => {
    setRoleMenuAnchorEl(null);
    setRoleMenuMember(null);
  };

  const queueRoleChange = (newRole) => {
    if (!roleMenuMember || !newRole) return;
    const oldRole = String(roleMenuMember.role || 'member').toLowerCase();
    if (newRole === oldRole) return;
    const nextAction = {
      type: 'changeRole',
      farmId: userData?.activeFarmId || '',
      memberUid: roleMenuMember.id,
      memberName: roleMenuMember.name,
      oldRole,
      newRole,
    };
    setPendingAction(nextAction);
    setDialogAction(nextAction);
    handleCloseRoleMenu();
  };

  const queueRemoveMember = (member) => {
    const nextAction = {
      type: 'removeMember',
      farmId: userData?.activeFarmId || '',
      memberUid: member.id,
      memberName: member.name,
    };
    setPendingAction(nextAction);
    setDialogAction(nextAction);
  };

  const closePendingAction = () => {
    setPendingAction(null);
  };

  const runPendingAction = async () => {
    if (!pendingAction || !userData?.activeFarmId) {
      setPendingAction(null);
      return;
    }

    const memberUid = pendingAction.memberUid;
    if (!memberUid) {
      setPendingAction(null);
      return;
    }

    setBusyMemberIds((prev) => ({ ...prev, [memberUid]: true }));
    try {
      if (pendingAction.type === 'changeRole') {
        if (!canManageMembers) {
          showError('Only owner/admin can change member roles.');
          return;
        }
        const targetFarmId = pendingAction.farmId || userData.activeFarmId;
        await updateDoc(doc(db, 'farms', targetFarmId, 'members', memberUid), {
          role: pendingAction.newRole,
        });
        showSuccess('Member role updated.');
      } else if (pendingAction.type === 'removeMember') {
        const isSelfRemoval = memberUid === uid;
        const targetMember = members.find((member) => member.id === memberUid);
        const targetRole = String(targetMember?.role || '').toLowerCase();
        const canRemoveTarget =
          isSelfRemoval
          || (canManageMembers && targetRole !== 'owner');

        if (!canRemoveTarget) {
          showError('You do not have permission to remove this member.');
          return;
        }
        await handleRemoveMember(memberUid, pendingAction.farmId || userData.activeFarmId);
      }
    } catch (error) {
      if (pendingAction.type === 'changeRole') {
        showError('Failed to update member role.');
      } else if (pendingAction.type === 'removeMember') {
        showError('Failed to remove member.');
      }
    } finally {
      setBusyMemberIds((prev) => ({ ...prev, [memberUid]: false }));
      setPendingAction(null);
    }
  };

  const inviteCode = farmData?.inviteCode || '';
  const currentFarmName = farmData?.farmName || userData?.farmName || 'N/A';
  const currentAddress = farmData?.address || userData?.address || 'N/A';
  const currentPhone = farmData?.phone || userData?.phone || 'N/A';
  const currentEmail = farmData?.email || 'N/A';
  const currentLogoUrl = farmData?.logoUrl || '';
  const canEditFarmDetails = memberRole === 'owner';
  const canManageLogo = memberRole === 'owner';
  const canManageMembers = memberRole === 'owner' || memberRole === 'admin';
  const canEditFarmSettings = memberRole === 'owner' || memberRole === 'admin';
  const canAccessOwnerOnlySections = memberRole === 'owner';
  const canAccessInviteCode = memberRole === 'owner' || memberRole === 'admin';
  const canShowRemoveForMember = (member) => {
    const isSelf = member?.id === uid;
    const targetRole = String(member?.role || '').toLowerCase();
    if (isSelf) {
      return targetRole !== 'owner';
    }
    return canManageMembers && targetRole !== 'owner';
  };

  const weightUnitLabel = weightUnit === 'oz' ? 'Ounces (oz)' : 'Grams (g)';
  const measureUnitLabel = measureUnit === 'in' ? 'Inches (in)' : 'Centimetres (cm)';
  const dateFormatLabel = dateFormat === 'MM-dd-yyyy' ? 'mm-dd-yyyy' : 'dd-mm-yyyy';
  const calendarWeekStartLabel = calendarWeekStart === 'monday' ? 'Monday' : 'Sunday';
  const currencyLabel = currency || '£';

  const roleLabel = (role) => {
    const raw = String(role || 'member').toLowerCase();
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const roleChipSx = (role) => {
    const lower = String(role || 'member').toLowerCase();
    if (lower === 'owner') {
      return {
        borderColor: 'tertiary.main',
        color: 'common.white',
        bgcolor: 'tertiary.main',
      };
    }
    if (lower === 'admin') {
      return {
        borderColor: 'warning.dark',
        color: 'common.white',
        bgcolor: 'warning.dark',
      };
    }
    if (lower === 'worker') {
      return {
        borderColor: 'secondary.dark',
        color: 'common.white',
        bgcolor: 'secondary.dark',
      };
    }
    return {
      borderColor: 'primary.dark',
      color: 'common.white',
      bgcolor: 'primary.dark',
    };
  };

  const formatJoinedAt = (joinedAt) => {
    if (!joinedAt?.toDate) return '';
    return joinedAt.toDate().toLocaleDateString('en-GB');
  };

  return (
    <>
    <Box
      sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        maxWidth: { xs: '100%', sm: '100%', md: '80%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
		        <Box>
          <Typography variant="h5" gutterBottom>
            Farm Details
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              backgroundColor: 'transparent',
            }}
          >
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell width="30%">Farm Name</TableCell>
                  <TableCell width="70%">
                    {!editingFarm ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 144px', alignItems: 'center', gap: 1 }}>
                        <Typography>{currentFarmName}</Typography>
                        <Box sx={{ width: 144, display: 'flex', justifyContent: 'flex-end' }}>
                          {canEditFarmDetails ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTempFarm(currentFarmName === 'N/A' ? '' : currentFarmName);
                                setEditingFarm(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 144px' }, alignItems: 'center', gap: 1 }}>
                        <Box>
                          <TextField
                            size="small"
                            value={tempFarm}
                            onChange={(e) => setTempFarm(e.target.value)}
                            onKeyDown={(e) => handleEnterSave(e, handleSaveFarm)}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: 144 }, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton
                            aria-label="Save farm name"
                            color="success"
                            size="small"
                            onClick={handleSaveFarm}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            aria-label="Cancel farm name edit"
                            color="error"
                            size="small"
                            onClick={() => setEditingFarm(false)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell width="30%">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Farm Address</span>
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: '0.8rem' }}>
                            This is for invoicing purposes only will not be used to contact you.
                          </Typography>
                        }
                      >
                        <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell width="70%">
                    {!editingAddress ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 144px', alignItems: 'start', gap: 1 }}>
                        <Typography>{currentAddress}</Typography>
                        <Box sx={{ width: 144, display: 'flex', justifyContent: 'flex-end' }}>
                          {canEditFarmDetails ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                const [street, city, county, postalCode] = (farmData?.address || userData?.address || ',,,')
                                  .split(',')
                                  .map((s) => s.trim());
                                setTempAddress({ street, city, county, postalCode });
                                setEditingAddress(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 144px' }, alignItems: 'start', gap: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TextField
                            size="small"
                            label="Street"
                            value={tempAddress.street}
                            onChange={(e) => setTempAddress({ ...tempAddress, street: e.target.value })}
                            onKeyDown={(e) => handleEnterSave(e, handleSaveAddress)}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="City"
                            value={tempAddress.city}
                            onChange={(e) => setTempAddress({ ...tempAddress, city: e.target.value })}
                            onKeyDown={(e) => handleEnterSave(e, handleSaveAddress)}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="County"
                            value={tempAddress.county}
                            onChange={(e) => setTempAddress({ ...tempAddress, county: e.target.value })}
                            onKeyDown={(e) => handleEnterSave(e, handleSaveAddress)}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Postal Code"
                            value={tempAddress.postalCode}
                            onChange={(e) => setTempAddress({ ...tempAddress, postalCode: e.target.value })}
                            onKeyDown={(e) => handleEnterSave(e, handleSaveAddress)}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: 144 }, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton
                            aria-label="Save farm address"
                            color="success"
                            size="small"
                            onClick={handleSaveAddress}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            aria-label="Cancel farm address edit"
                            color="error"
                            size="small"
                            onClick={() => setEditingAddress(false)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell width="30%">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Invoicing Phone Number</span>
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: '0.8rem' }}>
                            This is for invoicing purposes only and will not be used to contact you.
                          </Typography>
                        }
                      >
                        <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell width="70%">
                    {!editingPhone ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 144px', alignItems: 'center', gap: 1 }}>
                        <Typography>{currentPhone}</Typography>
                        <Box sx={{ width: 144, display: 'flex', justifyContent: 'flex-end' }}>
                          {canEditFarmDetails ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTempPhone(currentPhone === 'N/A' ? '' : currentPhone);
                                setEditingPhone(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 144px' }, alignItems: 'center', gap: 1 }}>
                        <Box>
                          <TextField
                            size="small"
                            value={tempPhone}
                            onChange={(e) => setTempPhone(e.target.value)}
                            onKeyDown={(e) => handleEnterSave(e, handleSavePhone)}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: 144 }, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton
                            aria-label="Save invoicing phone number"
                            color="success"
                            size="small"
                            onClick={handleSavePhone}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            aria-label="Cancel invoicing phone number edit"
                            color="error"
                            size="small"
                            onClick={() => setEditingPhone(false)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell width="30%">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Invoicing Email</span>
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: '0.8rem' }}>
                            This is for invoicing purposes only and will not be used to contact you.
                          </Typography>
                        }
                      >
                        <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell width="70%">
                    {!editingEmail ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 144px', alignItems: 'center', gap: 1 }}>
                        <Typography>{currentEmail}</Typography>
                        <Box sx={{ width: 144, display: 'flex', justifyContent: 'flex-end' }}>
                          {canEditFarmDetails ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTempEmail(currentEmail === 'N/A' ? '' : currentEmail);
                                setEditingEmail(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 144px' }, alignItems: 'center', gap: 1 }}>
                        <Box>
                          <TextField
                            size="small"
                            value={tempEmail}
                            onChange={(e) => setTempEmail(e.target.value)}
                            onKeyDown={(e) => handleEnterSave(e, handleSaveFarmEmail)}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: { xs: '100%', sm: 144 }, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton
                            aria-label="Save invoicing email"
                            color="success"
                            size="small"
                            onClick={handleSaveFarmEmail}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            aria-label="Cancel invoicing email edit"
                            color="error"
                            size="small"
                            onClick={() => setEditingEmail(false)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>

                {canManageLogo && (
                  <TableRow>
                    <TableCell width="30%">Farm Logo</TableCell>
                    <TableCell width="70%">
                      <Box>
                        <input
                          type="file"
                          accept=".png, .jpg, .jpeg, .webp"
                          id="logo-upload"
                          onChange={handleLogoUpload}
                          style={{ display: 'none' }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                          <label htmlFor="logo-upload">
                            <Button variant="contained" color="secondary" component="span">
                              {currentLogoUrl ? 'Change Logo' : 'Upload Logo'}
                            </Button>
                          </label>
                          {currentLogoUrl && (
                            <Button variant="text" color="error" onClick={handleDeleteLogo}>
                              <DeleteIcon />
                            </Button>
                          )}
                        </Box>

                        {currentLogoUrl && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Box
                              component="img"
                              src={currentLogoUrl}
                              alt="Farm Logo"
                              sx={{
                                width: { sm: 250, xs: 200 },
                                height: { sm: 250, xs: 200 },
                                objectFit: 'contain',
                                borderRadius: 4,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Allowed file types: PNG, JPG, JPEG, WEBP. Original image up to 20MB.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ my: 2 }} />

	          <Typography variant="h5" gutterBottom>
	        Farm Settings
	      </Typography>

      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }}
      >
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ border: 'none', fontWeight: 'bold', width: '40%' }}>
                Weight Unit
              </TableCell>
              <TableCell sx={{ border: 'none', width: '60%' }}>
                {canEditFarmSettings ? (
                  <Select value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)} variant="outlined" size="small" fullWidth>
                    <MenuItem value="g">Grams (g)</MenuItem>
                    <MenuItem value="oz">Ounces (oz)</MenuItem>
                  </Select>
                ) : (
                  <Typography>{weightUnitLabel}</Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: 'none', fontWeight: 'bold', width: '40%' }}>
                Measure Unit
              </TableCell>
              <TableCell sx={{ border: 'none', width: '60%' }}>
                {canEditFarmSettings ? (
                  <Select value={measureUnit} onChange={(e) => setMeasureUnit(e.target.value)} variant="outlined" size="small" fullWidth>
                    <MenuItem value="cm">Centimetres (cm)</MenuItem>
                    <MenuItem value="in">Inches (in)</MenuItem>
                  </Select>
                ) : (
                  <Typography>{measureUnitLabel}</Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: 'none', fontWeight: 'bold', width: '40%' }}>
                Date Format
              </TableCell>
              <TableCell sx={{ border: 'none', width: '60%' }}>
                {canEditFarmSettings ? (
                  <Select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} variant="outlined" size="small" fullWidth>
                    <MenuItem value="dd-MM-yyyy">dd-mm-yyyy</MenuItem>
                    <MenuItem value="MM-dd-yyyy">mm-dd-yyyy</MenuItem>
                  </Select>
                ) : (
                  <Typography>{dateFormatLabel}</Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: 'none', fontWeight: 'bold', width: '40%' }}>
                Calendar Week Start
              </TableCell>
              <TableCell sx={{ border: 'none', width: '60%' }}>
                {canEditFarmSettings ? (
                  <Select
                    value={calendarWeekStart}
                    onChange={(e) => setCalendarWeekStart(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="sunday">Sunday</MenuItem>
                    <MenuItem value="monday">Monday</MenuItem>
                  </Select>
                ) : (
                  <Typography>{calendarWeekStartLabel}</Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: 'none', fontWeight: 'bold', width: '40%' }}>
                Currency
              </TableCell>
              <TableCell sx={{ border: 'none', width: '60%' }}>
                {canEditFarmSettings ? (
                  <Select value={currency} onChange={(e) => setCurrency(e.target.value)} variant="outlined" size="small" fullWidth>
                    <MenuItem value="£">£</MenuItem>
                    <MenuItem value="$">$</MenuItem>
                    <MenuItem value="€">€</MenuItem>
                  </Select>
                ) : (
                  <Typography>{currencyLabel}</Typography>
                )}
              </TableCell>
            </TableRow>
            {canEditFarmSettings ? (
              <TableRow>
                <TableCell colSpan={2} sx={{ border: 'none' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button variant="contained" color="secondary" onClick={handleSaveSettings}>
                      Save Settings
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
	      </TableContainer>

	        </Box>

		        <Box>
              {canAccessInviteCode ? (
                <>
		              <Typography variant="h5" gutterBottom>
		                Invite Code
		              </Typography>
	              <TableContainer
	                component={Paper}
	                sx={{
	                  border: '1px solid',
	                  borderColor: 'divider',
	                  boxShadow: 'none',
	                  backgroundColor: 'transparent',
	                }}
	              >
	                <Table
	                  size="small"
	                >
                  <TableBody>
                    <TableRow>
	                      <TableCell sx={{ width: '60%' }}>
	                        <Typography sx={{ letterSpacing: 2, fontSize: '2rem' }}>
	                          {formatInviteCode(inviteCode)}
	                        </Typography>
	                      </TableCell>
	                      <TableCell sx={{ width: '10%' }}>
	                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
	                          <Button
	                            variant="text"
	                            color="secondary"
	                            onClick={handleCopyInviteCode}
	                            disabled={!inviteCode}
	                          >
	                            <ContentCopyIcon />
	                          </Button>
	                        </Box>
	                      </TableCell>
                      <TableCell sx={{ width: '30%' }}>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={handleRegenerateInviteCode}
                          fullWidth
                        >
                          Re-generate
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ my: 2 }} />
                </>
              ) : null}

          <Typography variant="h5" gutterBottom>
            Members
          </Typography>
	          <TableContainer
              component={Paper}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }}
            >
	            <Table
	              size="small"
	            >
	              <TableHead
                  sx={{
                    '& .MuiTableCell-root': {
                      backgroundColor: 'primary.main',
                      color: 'common.white',
                    },
                  }}
                >
	                <TableRow>
                    <TableCell sx={{ width: '8%' }} />
	                  <TableCell sx={{ width: '50%' }}>Name</TableCell>
	                  <TableCell sx={{ width: '20%' }}>Role</TableCell>
	                  <TableCell sx={{ width: '20%' }}>Joined</TableCell>
	                  <TableCell sx={{ width: '10%' }}>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      No members found.
                    </TableCell>
                  </TableRow>
                ) : (
	                  members.map((member) => (
	                    <TableRow key={member.id}>
                        <TableCell sx={{ width: '8%' }}>
                          <UserAvatar user={null} userData={{ photoURL: member.photoURL || '' }} size={32} />
                        </TableCell>
	                      <TableCell sx={{ width: '45%' }}>{member.name}</TableCell>
	                      <TableCell sx={{ width: '20%' }}>
	                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={roleLabel(member.role)}
                            sx={roleChipSx(member.role)}
                          />
                          {canManageMembers && String(member.role || '').toLowerCase() !== 'owner' ? (
	                            <IconButton
	                              size="small"
	                              onClick={(event) => handleOpenRoleMenu(event, member)}
	                              disabled={!!busyMemberIds[member.id]}
	                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </TableCell>
	                      <TableCell sx={{ width: '20%' }}>{formatJoinedAt(member.joinedAt)}</TableCell>
		                      <TableCell sx={{ width: '15%' }}>
	                        {canShowRemoveForMember(member) ? (
	                          <Button
	                            variant="text"
	                            color="error"
	                            onClick={() => queueRemoveMember(member)}
                              disabled={!!busyMemberIds[member.id]}
	                          >
	                            <PersonRemoveIcon />
	                          </Button>
	                        ) : null}
	                      </TableCell>
                    </TableRow>
                  ))
                )}
	              </TableBody>
	            </Table>
	          </TableContainer>
	          <Box
	            sx={{
	              mt: 1.5,
	              p: 1.5,
	              border: '1px solid',
	              borderColor: 'divider',
	              borderRadius: 1,
	              backgroundColor: 'background.paper',
	            }}
	          >
	            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
	              Role Guide
	            </Typography>
		              <Box sx={{ display: 'grid', gap: 0.75 }}>
	              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
	                <Chip size="small" variant="outlined" label="Owner" sx={roleChipSx('owner')} />
	                <Typography variant="body2">Full control of farm settings, members, and operational data.</Typography>
	              </Box>
	              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
	                <Chip size="small" variant="outlined" label="Admin" sx={roleChipSx('admin')} />
	                <Typography variant="body2">Can manage members and most farm operations.</Typography>
	              </Box>
	              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
	                <Chip size="small" variant="outlined" label="Worker" sx={roleChipSx('worker')} />
	                <Typography variant="body2">Can create and update operational records.</Typography>
	              </Box>
				              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				                <Chip size="small" variant="outlined" label="Member" sx={roleChipSx('member')} />
				                <Typography variant="body2">Read-only access to farm data.</Typography>
				      </Box>
				    </Box>
		          </Box>

		          {canAccessOwnerOnlySections ? (
		            <>
		              <Box sx={{ my: 2 }} />

		              <Typography variant="h5" gutterBottom>
		                Backup
		              </Typography>

		              <TableContainer
		                component={Paper}
		                sx={{
		                  border: '1px solid',
		                  borderColor: 'divider',
		                  boxShadow: 'none',
		                  backgroundColor: 'transparent',
		                }}
		              >
		                <Table
		                  size="small"
		                  sx={{
		                    '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider', p: 2 },
		                  }}
		                >
		                  <TableBody>
		                    <TableRow>
		                      <TableCell sx={{ width: '50%', verticalAlign: 'top' }}>
		                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
		                          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
		                            <DownloadIcon fontSize="small" />
		                            <span>Export Backups</span>
		                          </Typography>
		                          <Typography variant="body2" sx={{ minHeight: 60 }}>
		                            Download a backup of your farm data and/or invoices. Media and user profile data are excluded.
		                          </Typography>
		                          <Box
		                            sx={{
		                              display: 'flex',
		                              flexDirection: 'column',
		                              alignItems: 'flex-start',
		                              gap: 1,
		                            }}
		                          >
		                            <Button
		                              variant="contained"
		                              color="warning"
		                              startIcon={<DownloadIcon />}
		                              onClick={handleDownloadBackup}
		                              disabled={exportingBackup || !hasCollectionData}
		                              sx={{ width: 260, minHeight: 36, whiteSpace: 'nowrap' }}
		                            >
		                              {exportingBackup ? 'Preparing Backup...' : 'Export Data'}
		                            </Button>
		                            <Button
		                              variant="outlined"
		                              color="warning"
		                              startIcon={
		                                exportingInvoices
		                                  ? <CircularProgress size={18} color="inherit" />
		                                  : <DownloadIcon />
		                              }
		                              onClick={handleDownloadInvoiceBackup}
		                              disabled={exportingInvoices || !invoices?.length}
		                              sx={{ width: 260, minHeight: 36, whiteSpace: 'nowrap' }}
		                            >
		                              {exportingInvoices ? 'Preparing Invoices...' : 'Export Invoices'}
		                            </Button>
		                          </Box>
		                        </Box>
		                      </TableCell>
		                      <TableCell sx={{ width: '50%', verticalAlign: 'top' }}>
		                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
		                          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
		                            <UploadIcon fontSize="small" />
		                            <span>Import Backups</span>
		                          </Typography>
		                          <Typography variant="body2" sx={{ minHeight: 60 }}>
		                            Import a .micgro backup file to restore farm data and/or invoices.
		                          </Typography>
		                          <Box
		                            sx={{
		                              display: 'flex',
		                              flexDirection: 'column',
		                              alignItems: 'flex-start',
		                              gap: 1,
		                            }}
		                          >
		                            <input
		                              type="file"
		                              accept=".micgro,.zip,application/zip,application/octet-stream"
		                              id="farm-backup-upload"
		                              onChange={handleUploadBackup}
		                              disabled={importingBackup}
		                              style={{ display: 'none' }}
		                            />
		                            <label htmlFor="farm-backup-upload">
		                              <Button
		                                variant="contained"
		                                color="warning"
		                                component="span"
		                                startIcon={<UploadIcon />}
		                                disabled={importingBackup}
		                                sx={{ width: 260, minHeight: 36, whiteSpace: 'nowrap' }}
		                              >
		                                {importingBackup ? 'Importing Backup...' : 'Import Data'}
		                              </Button>
		                            </label>
		                            <input
		                              type="file"
		                              accept=".micgro,.zip,application/zip,application/octet-stream"
		                              id="invoice-backup-upload"
		                              onChange={handleUploadInvoiceBackup}
		                              disabled={importingInvoices}
		                              style={{ display: 'none' }}
		                            />
		                            <label htmlFor="invoice-backup-upload">
		                                <Button
		                                  variant="outlined"
		                                  color="warning"
		                                  component="span"
		                                  startIcon={<UploadIcon />}
		                                  disabled={importingInvoices}
		                                  sx={{ width: 260, minHeight: 36, whiteSpace: 'nowrap' }}
		                                >
		                                  {importingInvoices ? 'Importing Invoices...' : 'Import Invoices'}
		                                </Button>
		                            </label>
		                          </Box>
		                        </Box>
		                      </TableCell>
		                    </TableRow>
		                  </TableBody>
		                </Table>
		              </TableContainer>
		            </>
		          ) : null}

		          {canAccessOwnerOnlySections ? (
		            <Box sx={{ mt: 3 }}>
		              <Typography variant="h5" gutterBottom color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		                <span>Danger Zone</span>
		                <WarningAmberIcon sx={{ color: 'error.main' }} />
		              </Typography>

		              <Paper
		                sx={{
		                  p: 2,
		                  border: '1px solid',
		                  borderColor: 'divider',
		                  borderLeft: '4px solid',
		                  borderLeftColor: 'error.main',
		                  boxShadow: 'none',
		                  backgroundColor: 'transparent',
		                }}
		              >
		                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		                  <Box
		                    sx={{
		                      display: 'grid',
		                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 190px' },
		                      gap: 2,
		                      alignItems: 'center',
		                    }}
		                  >
		                    <Box>
		                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
		                        Delete farm data
		                      </Typography>
		                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
		                        Permanently delete all operational farm data. This action cannot be undone.
		                      </Typography>
		                    </Box>
		                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
		                      <Button variant="contained" color="error" onClick={handleDeleteFarmData} sx={{ minWidth: 180 }}>
		                        Delete Farm Data
		                      </Button>
		                    </Box>
		                  </Box>

		                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

		                  <Box
		                    sx={{
		                      display: 'grid',
		                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 190px' },
		                      gap: 2,
		                      alignItems: 'center',
		                    }}
		                  >
		                    <Box>
		                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
		                        Delete customers
		                      </Typography>
		                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
		                        Permanently delete all customer records for this farm.
		                      </Typography>
		                    </Box>
		                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
		                      <Button variant="contained" color="error" onClick={handleDeleteCustomers} sx={{ minWidth: 180 }}>
		                        Delete Customers
		                      </Button>
		                    </Box>
		                  </Box>

		                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

		                  <Box
		                    sx={{
		                      display: 'grid',
		                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 190px' },
		                      gap: 2,
		                      alignItems: 'center',
		                    }}
		                  >
		                    <Box>
		                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
		                        Delete invoices
		                      </Typography>
		                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
		                        Permanently delete all invoice files and invoice records for this farm.
		                      </Typography>
		                    </Box>
		                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
		                      <Button variant="contained" color="error" onClick={handleDeleteInvoices} sx={{ minWidth: 180 }}>
		                        Delete Invoices
		                      </Button>
		                    </Box>
		                  </Box>
		                </Box>
		              </Paper>
		            </Box>
		          ) : null}
				          <Menu
				            anchorEl={roleMenuAnchorEl}
				            open={Boolean(roleMenuAnchorEl)}
			            onClose={handleCloseRoleMenu}
			          >
		            {roleOptions.map((role) => {
		              const isCurrent = String(roleMenuMember?.role || 'member').toLowerCase() === role;
		              return (
		                <MenuItem
		                  key={role}
		                  disabled={isCurrent || !!busyMemberIds[roleMenuMember?.id]}
		                  onClick={() => queueRoleChange(role)}
		                >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={roleLabel(role)}
                        sx={roleChipSx(role)}
                      />
		                </MenuItem>
		              );
		            })}
		          </Menu>

		          <ConfirmationDialog
	            open={Boolean(pendingAction)}
	            title={
                isPendingRoleChange
                  ? 'Change member role?'
                  : isPendingRemoveMember
                    ? 'Remove member?'
                    : ''
              }
		            message={
		              isPendingRoleChange
		                ? `${activeDialogAction?.memberName || 'Member'} will change from ${roleLabel(
		                    activeDialogAction?.oldRole
		                  )} to ${roleLabel(activeDialogAction?.newRole)}.`
		                : isPendingRemoveMember
	                    ? `Remove ${activeDialogAction?.memberName || 'this member'} from this farm?`
	                    : ''
		            }
	            onConfirm={runPendingAction}
	            onClose={closePendingAction}
		            confirmLabel={
	                isPendingRoleChange ? 'Change role' : isPendingRemoveMember ? 'Remove member' : 'Accept'
	              }
		          />
	              <ConfirmationDialog
	                open={farmDangerDialogOpen}
	                title={farmDangerTitle}
	                message={farmDangerMessage}
	                onConfirm={handleConfirmFarmDanger}
	                onClose={() => setFarmDangerDialogOpen(false)}
	                requiredWord="MICROGREEN"
	              />
			        </Box>
			        </Box>
			      </Box>
        <Backdrop
          open={exportingBackup}
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 998 }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Preparing backup...
          </Typography>
        </Backdrop>

        <Backdrop
          open={importingBackup}
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 997 }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Importing backup...
          </Typography>
        </Backdrop>
        </>
		  );
};

export default Farm;









